import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Empty, ErrorBanner, Spinner } from '@shop/ui'
import { TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { AttendanceRow } from '../pos/contracts'

const clock = new Intl.DateTimeFormat('ar-JO-u-nu-latn', { timeZone: 'Asia/Amman', hour: 'numeric', minute: '2-digit' })

function time(iso: string | null): string {
  return iso ? clock.format(new Date(iso)) : ''
}

/**
 * الدوامُ اليوم — <b>طلبُ المالك ٢٠٢٦-١٠-٠٨</b>.
 *
 * ⚠ <b>كلُّ الموظّفين لا المُسجِّل وحدَه</b>: الجهازُ واحد، والحسابُ المفتوحُ عليه
 *   حسابُ من فتح الورديّة. فيكبس كلٌّ اسمَه «وصلت» حين يصل و«روّحت» حين يروّح،
 *   والمالكُ يراجع ويعدّل من لوحة الإدارة.
 */
export default function AttendancePanel() {
  const queries = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)

  const today = useQuery({
    queryKey: ['attendance-today'],
    queryFn: ({ signal }) => pos.attendanceToday(signal),
    staleTime: 0,
  })

  const press = async (row: AttendanceRow, way: 'in' | 'out') => {
    setBusy(row.userId)
    setError(null)

    try {
      await pos.punch(row.userId, way)
      await queries.invalidateQueries({ queryKey: ['attendance-today'] })
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="grid gap-2">
      <h3 className="text-sm font-semibold">الدوامُ اليوم</h3>

      <ErrorBanner error={error ?? today.error} />

      {today.isPending ? (
        <Spinner />
      ) : (today.data ?? []).length === 0 ? (
        <Empty>لا موظّفين مسجّلين.</Empty>
      ) : (
        <ul className="grid gap-2">
          {(today.data ?? []).map((row) => {
            const inside = row.checkIn !== null && row.checkOut === null

            return (
              <li key={row.userId} className="flex items-center gap-2 rounded-xl border border-line p-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{row.fullNameAr}</p>
                  <p className="text-xs text-ink-soft">
                    {row.checkIn === null
                      ? 'لم يصل بعد'
                      : inside
                        ? `وصل ${time(row.checkIn)}`
                        : `${time(row.checkIn)} – ${time(row.checkOut)} · ${row.workedHours ?? 0} ساعة`}
                    {row.overtimeHours > 0 && <b className="text-ok"> · زيادة {row.overtimeHours} س</b>}
                  </p>
                </div>

                {inside ? (
                  <TouchButton disabled={busy === row.userId} onClick={() => void press(row, 'out')}>
                    روّحت
                  </TouchButton>
                ) : (
                  <TouchButton tone="ok" disabled={busy === row.userId} onClick={() => void press(row, 'in')}>
                    {row.checkOut ? 'رجعت' : 'وصلت'}
                  </TouchButton>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
